import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useDeviceSize } from '@vmos2/providers';
import debounce from 'lodash/debounce';
import { ConditionalWrapper } from '@vmos2/utils';

import { Tooltip } from '../tooltip';
import { IconButton } from '../buttons';
import { DecreaseQuantityIcon } from './components/DecreaseQuantityIcon';
import { CounterComponent } from './CounterComponent';
import { QuantityContainer } from './components/QuantityContainer';

type Props = {
  id?: string,
  className?: ?string,
  onIncrease: Function,
  onDecrease: Function,
  hasNextStep: boolean,
  hasPreviousStep?: boolean,
  step: number,
  counter: number,
  hasCounter: boolean,
  reachedMax?: boolean,
  filled?: boolean,
  hasCounterAsButton?: Boolean,
  size?: 'xsmall' | 'small' | 'large',
  isEditable?: boolean,
  isEditing?: boolean,
  onChangeAmount?: Function,
  onInputFocus?: Function,
  onInputBlur?: Functioon,
  enteredAmountExceeded?: boolean,
  buttonWithoutSpace?: boolean,
  itemName?: string,
  title?: string | React.ReactNode | JSX.Element,
  isCentered?: boolean,
};

/**
 * ### Theme Example
 * ```
 * "default" {
 *   ...
 *   "components": {
 *     ...
 *     "QuantityCounter": {
 *       "default": {
 *         "unitLabel": false | true
 *       }
 *     }
 *   }
 * }
 * ```
 */

export const QuantityCounter = ({
  id,
  className,
  itemName,
  onDecrease,
  onIncrease,
  step,
  hasNextStep,
  hasPreviousStep = true,
  counter = 1,
  hasCounter = true,
  reachedMax,
  filled,
  hasCounterAsButton,
  size,
  isEditable = false,
  isEditing,
  onChangeAmount,
  onInputFocus,
  onInputBlur,
  enteredAmountExceeded,
  buttonWithoutSpace,
  title,
  isCentered,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation('');
  const { isMobile } = useDeviceSize();
  const [accessibilityMessage, setAccessibilityMessage] = useState('');
  const counterRef = useRef(counter);
  const timeoutRef = useRef(null);

  const debouncedAnnounce = useMemo(
    () =>
      debounce((prev: number, next: number, reachedMax: boolean) => {
        if (prev > next) {
          setAccessibilityMessage(t('buttons:quantityDecreased', { quantity: next }));

          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          timeoutRef.current = window.setTimeout(() => {
            setAccessibilityMessage('');
          }, 500);
        } else if (prev < next) {
          setAccessibilityMessage(
            `${t('buttons:quantityIncreased', { quantity: next })} ${reachedMax ? t('alerts:maxSelected') : ''}`
          );

          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          timeoutRef.current = window.setTimeout(() => {
            setAccessibilityMessage('');
          }, 500);
        }
        counterRef.current = next;
      }, 500),
    [t]
  );

  useEffect(() => {
    debouncedAnnounce(counterRef.current, counter, reachedMax);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      debouncedAnnounce.cancel();
    };
  }, [counter, debouncedAnnounce, reachedMax]);

  return (
    <>
      <div
        aria-atomic="true"
        aria-live="polite"
        className="hide-text"
        data-test="quantity-counter-accessibility-announcement"
      >
        {accessibilityMessage}
      </div>

      <QuantityContainer
        className={className}
        hasCounterAsButton={hasCounterAsButton}
        size={size}
        isCentered={isCentered}
        decreaseButton={
          <DecreaseQuantityIcon
            ariaLabel={
              step > 0 ? t('buttons:decreaseQuantity', { quantity: counter }) : t('buttons:remove', { name: itemName })
            }
            buttonWithoutSpace={buttonWithoutSpace}
            currentQuantity={counter}
            disabled={!hasPreviousStep || (hasCounter && step === 1 && counter === 1)}
            icon={step > 0 ? 'Minus' : 'Trash'}
            onDecrease={onDecrease}
            size={size || (isMobile ? 'xsmall' : 'small')}
          />
        }
        counter={
          <>
            {((hasCounter && !!counter) || isEditing) && (
              <CounterComponent
                id={id}
                size={size}
                isEditable={isEditable}
                isEditing={isEditing}
                onChangeAmount={onChangeAmount}
                filled={filled}
                quantity={counter}
                onInputFocus={onInputFocus}
                onInputBlur={onInputBlur}
                reachedMax={reachedMax}
                enteredAmountExceeded={enteredAmountExceeded}
              />
            )}

            {title}

            <ConditionalWrapper
              condition={!!reachedMax}
              wrapper={children => (
                <Tooltip content={<span>{t('alerts:maxSelected')}</span>}>
                  <span>{children}</span>
                </Tooltip>
              )}
            >
              <IconButton
                onClick={onIncrease}
                ariaDisabled={!hasNextStep || !!reachedMax}
                dataTest="increase-quantity-btn"
                icon="Plus"
                backgroundColor={theme.color.white}
                borderColor={theme.color.lightGrey}
                tooltip={t('buttons:addAnother')}
                touchTooltip={false}
                size={size || (isMobile ? 'xsmall' : 'small')}
                mode="light"
                withoutSpace={buttonWithoutSpace}
                ariaLabel={t('buttons:increaseQuantity', { quantity: counter })}
              />
            </ConditionalWrapper>
          </>
        }
        counterAsButton={
          <Tooltip content={<span>{t(reachedMax ? 'alerts:maxSelected' : 'alerts:addMore')}</span>} touch={reachedMax}>
            <span>
              <CounterComponent
                filled
                quantity={counter}
                onClick={onIncrease}
                disabled={!hasNextStep || reachedMax}
                dataTest="increase-quantity-btn"
                size={size}
              />
            </span>
          </Tooltip>
        }
      />
    </>
  );
};
